
import {defineComponent} from "vue";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {ContactFormContentRestDtoModel} from "@/models/ContactFormContentRestDtoModel";
import {CbJsonContactFormEmailSettings, CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {DpException} from "@/exception/DpException";
import {PharmacyInformationRestService} from "@/services/rest/cms-pharmacy/PharmacyInformationRestService";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import {
  CbJsonContactFormEmailSettingsModel
} from "@/models/api/pharma-cms-content-block/CbJsonContactFormEmailSettingsModel";
import {EmailAddressRestDtoModel} from "@/models/api/pharma-cms-content-block/EmailAddressRestDtoModel";
import AdvancedPanel from "@/components/UI/AdvancedPanel.vue";
import AdvancedPanelSwitcher from "@/components/UI/AdvancedPanelSwitcher.vue";
import InputText from "@/components/UI/InputText.vue";

export default defineComponent({
  name: "CbContactForm",
  components: {AdvancedPanelSwitcher, AdvancedPanel, InputSelect, BaseSpinner, InputText},
  extends: CbStructureMixin,
  data: () => ({
    contentBlock: ContactFormContentRestDtoModel.createWithDefaults(),
    emailSettings: null as CbJsonContactFormEmailSettings | null,
    pharmacyOptions: [] as CodeDescriptionRestDto[],
    isAdvancedEmailSettings: false,
  }),
  methods: {
    async find(payload: FindNormalContentBlockPayload): Promise<ContactFormContentRestDtoModel> {
      return await NormalContentBlockRestService.getInstance().findContactForm(payload.type, payload.id);
    },
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      return await NormalContentBlockRestService.getInstance().createContactForm(payload.cb as ContactFormContentRestDtoModel, payload.type);
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      await NormalContentBlockRestService.getInstance().saveContactForm(payload.cb as ContactFormContentRestDtoModel, payload.type, payload.id);
    },
    async afterReloadContent(): Promise<void> {
      await this.findPharmacies();

      if (!this.contentBlock.pharmacy_code) {
        this.contentBlock.pharmacy_code = this.pharmacyOptions[0].code;
      }
      if (this.contentBlock.email_settings !== null) {
        this.emailSettings = this.contentBlock.email_settings as CbJsonContactFormEmailSettingsModel;
        this.isAdvancedEmailSettings = true;
      }
    },

    beforeSubmitData(): void {
      if (!this.contentBlock.pharmacy_code) {
        throw [new DpException(this.$t('Pharmacy needs to be selected.'))]
      }
      if (this.emailSettings === null) {
        this.contentBlock.email_settings = null;
      } else {
        if (this.emailSettings.subject === '') {
          throw [new DpException(this.$t('Subject is required.'))]
        }
        if (this.emailSettings.to.email === '') {
          throw [new DpException(this.$t('To email is required.'))]
        }
        this.contentBlock.email_settings = this.emailSettings;
      }
    },
    getDefaultCbModel(): ContactFormContentRestDtoModel {
      return ContactFormContentRestDtoModel.createWithDefaults();
    },
    toggleAdvancedEmailSettings(): void {
      if (!this.isAdvancedEmailSettings && (this.emailSettings === null || this.emailSettings === undefined)) {
        this.emailSettings = CbJsonContactFormEmailSettingsModel.createWithDefaults();
      }
      this.isAdvancedEmailSettings = !this.isAdvancedEmailSettings;
    },
    clearEmailSettings(): void {
      this.contentBlock.email_settings = null;
      this.emailSettings = null;
      this.isAdvancedEmailSettings = false;
    },
    getCcEmail(): string {
      return this.emailSettings && this.emailSettings.cc ? this.emailSettings.cc.email : '';
    },
    getCcName(): string {
      return this.emailSettings && this.emailSettings.cc ? this.emailSettings.cc.name || '' : '';
    },
    updateCcEmail(value: string): void {
      if (!this.emailSettings) return;

      this.onChange();

      if (!value) {
        if (this.emailSettings.cc && !this.emailSettings.cc.name) {
          this.emailSettings.cc = null;
        } else if (this.emailSettings.cc) {
          this.emailSettings.cc.email = '';
        }
        return;
      }

      if (!this.emailSettings.cc) {
        this.emailSettings.cc = new EmailAddressRestDtoModel(value);
      } else {
        this.emailSettings.cc.email = value;
      }
    },
    updateCcName(value: string): void {
      if (!this.emailSettings) return;

      this.onChange();

      if (!this.emailSettings.cc && value) {
        this.emailSettings.cc = new EmailAddressRestDtoModel('');
        this.emailSettings.cc.name = value;
      } else if (this.emailSettings.cc) {
        this.emailSettings.cc.name = value || null;
      }
    },
    getBccEmail(): string {
      return this.emailSettings && this.emailSettings.bcc ? this.emailSettings.bcc.email : '';
    },
    getBccName(): string {
      return this.emailSettings && this.emailSettings.bcc ? this.emailSettings.bcc.name || '' : '';
    },
    updateBccEmail(value: string): void {
      if (!this.emailSettings) return;

      this.onChange();

      if (!value) {
        if (this.emailSettings.bcc && !this.emailSettings.bcc.name) {
          this.emailSettings.bcc = null;
        } else if (this.emailSettings.bcc) {
          this.emailSettings.bcc.email = '';
        }
        return;
      }

      if (!this.emailSettings.bcc) {
        this.emailSettings.bcc = new EmailAddressRestDtoModel(value);
      } else {
        this.emailSettings.bcc.email = value;
      }
    },
    updateBccName(value: string): void {
      if (!this.emailSettings) return;

      this.onChange();

      if (!this.emailSettings.bcc && value) {
        this.emailSettings.bcc = new EmailAddressRestDtoModel('');
        this.emailSettings.bcc.name = value;
      } else if (this.emailSettings.bcc) {
        this.emailSettings.bcc.name = value || null;
      }
    },
    async findPharmacies(): Promise<void> {
      try {
        this.pharmacyOptions = await PharmacyInformationRestService
          .getInstance()
          .findPharmacies(true);
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
      }
    }
  }
});
